import { useCallback } from 'react'
import type { HotelMapViewData, ExperienceMapViewData, OfferMapSuggestionData, TripStop } from 'tripPlanner/api/types'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import TripPlanCanvas from '../components/TripPlanCanvas'
import DayItineraryCanvas from '../components/DayItineraryCanvas'
import { DayItineraryOverlay } from '../styles/layoutStyles'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import { useItineraryFingerprint } from '../hooks/useItineraryFingerprint'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'

interface Props {
  /** When false, hooks still run (preserving fingerprint tracking) but JSX returns null */
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
}

function ItineraryContainer({ visible, selectedDayItineraryId, selectedStop }: Props) {
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const {
    itineraryData,
    hasItineraryData,
    enrichingStopIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
  } = useItineraryContext()

  // ── Fingerprint tracking (dispatches 'itinerary_changed' on structural changes) ──

  useItineraryFingerprint({
    itineraryData,
    hasItineraryData,
    dispatch: eventBus.dispatch,
  })

  // ── Callback wrappers dispatching cross-container events ──

  const handleLearnMoreClick = useCallback((data: { googlePlaceId: string; placeName: string; countryName?: string; stopId?: number }) => {
    eventBus.dispatch({ type: 'open_detail_drawer', ...data })
  }, [eventBus])

  const handleExploreMapClick = useCallback((coordinates: { lat: number; lng: number }, source: LEExploreCategorySource) => {
    eventBus.dispatch({ type: 'show_explore_on_map', coordinates, source })
  }, [eventBus])

  const dispatchOfferSuggestion = useCallback((data: OfferMapSuggestionData) => {
    eventBus.dispatch({ type: 'show_offer_suggestion_on_map', data })
  }, [eventBus])

  const handleShowMap = useCallback((data: HotelMapViewData) => {
    const offer = data.offers.find(item => item.coordinates)
    if (!offer?.coordinates) return
    dispatchOfferSuggestion({
      offerId: offer.id,
      name: offer.name,
      coordinates: offer.coordinates,
      offerType: 'hotel',
    })
  }, [dispatchOfferSuggestion])

  const handleShowExperienceOnMap = useCallback((data: ExperienceMapViewData) => {
    const offer = data.offers.find(item => item.coordinates)
    if (!offer?.coordinates) return
    dispatchOfferSuggestion({
      offerId: offer.id,
      name: offer.name,
      coordinates: offer.coordinates,
      offerType: 'experience',
    })
  }, [dispatchOfferSuggestion])

  const handleAskAIForDay = useCallback((dayNumber: number, dayTitle: string, date?: string) => {
    eventBus.dispatch({ type: 'ask_ai_for_day', dayNumber, title: dayTitle, date })
  }, [eventBus])

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    if (!hotel.geoLocation) return
    dispatchOfferSuggestion({
      offerId: hotel.id,
      name: hotel.name,
      coordinates: hotel.geoLocation,
      offerType: 'hotel',
    })
  }, [dispatchOfferSuggestion])

  // ── Render ──

  // Convention exception: "Components should not determine whether they render"
  // This container must stay mounted so the fingerprint tracking effect detects
  // itinerary changes regardless of which view is active. The parent controls
  // visibility via the `visible` prop; the child only gates JSX output.
  if (!visible) return null

  const stopId = selectedStop?.id
  const shouldShowDayItinerary = selectedDayItineraryId && selectedStop && selectedStop.days.length > 0

  return (
    <>
      <TripPlanCanvas
        data={itineraryData}
        onLearnMoreClick={handleLearnMoreClick}
        onRefetchItinerary={refetchCurrentChatItinerary}
        onSendMessage={sendMessage}
        onExploreMap={handleExploreMapClick}
        onShowMap={handleShowMap}
        onShowExperienceOnMap={handleShowExperienceOnMap}
        enrichingStopIds={enrichingStopIds}
        onAskAI={handleAskAIForDay}
        scrollTarget={scrollTarget}
        onScrollComplete={clearScrollTarget}
      />
      {shouldShowDayItinerary && (
        <DayItineraryOverlay>
          <DayItineraryCanvas
            data={itineraryData}
            stopId={stopId}
            onSendMessage={sendMessage}
            onShowMap={handleOfferShowMap}
            onShowExperienceOnMap={handleShowExperienceOnMap}
          />
        </DayItineraryOverlay>
      )}
    </>
  )
}

export default ItineraryContainer
